// Asset vs Liability classifier — the foundational lesson. The one rule that makes
// every future venture meaningful: does it put money IN your pocket (asset) or
// take money OUT (liability)? Correctly-spotted assets can be claimed into your
// portfolio, which moves the Freedom Meter — tying the lesson to the goal.

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { LifeAsset } from '@biz-kid/engine';
import { Card, Eyebrow, Button, money } from '../ui/kit.js';
import { useLifeDispatch, newId } from './store.js';

type Item = {
  id: string;
  name: string;
  icon: string;
  isAsset: boolean;
  type: string;
  value: number;
  monthlyIncome: number;
  monthlyCost: number;
  why: string;
};

const DECK: Item[] = [
  { id: 'rental', name: 'A room you rent out', icon: '🏠', isAsset: true, type: 'rental', value: 800, monthlyIncome: 45, monthlyCost: 5, why: 'Tenants pay you rent every month — money in.' },
  { id: 'sneakers', name: 'Designer sneakers', icon: '👟', isAsset: false, type: 'lifestyle', value: 150, monthlyIncome: 0, monthlyCost: 0, why: 'Cool — but they only lose value and earn nothing.' },
  { id: 'vending', name: 'A course you made', icon: '📚', isAsset: true, type: 'vendingMachine', value: 500, monthlyIncome: 35, monthlyCost: 0, why: 'New fans buy it while you sleep — money in.' },
  { id: 'phone', name: 'New phone on contract', icon: '📱', isAsset: false, type: 'phone', value: 400, monthlyIncome: 0, monthlyCost: 30, why: 'A bill arrives every month — money out.' },
  { id: 'shares', name: 'Dividend shares', icon: '📈', isAsset: true, type: 'stocks', value: 600, monthlyIncome: 20, monthlyCost: 0, why: 'Companies pay you a slice of their profit.' },
  { id: 'car', name: 'A financed sports car', icon: '🏎️', isAsset: false, type: 'carLoan', value: 5000, monthlyIncome: 0, monthlyCost: 120, why: 'A big payment every month — drains your pocket.' },
  { id: 'carwash', name: 'A car-wash business', icon: '🚗', isAsset: true, type: 'business', value: 1000, monthlyIncome: 70, monthlyCost: 10, why: 'A system that earns even when you’re not there.' },
  { id: 'skins', name: 'Game skins', icon: '🎮', isAsset: false, type: 'lifestyle', value: 60, monthlyIncome: 0, monthlyCost: 0, why: 'Fun — but they put nothing back in your pocket.' },
];

export function AssetLiabilityClassifier() {
  const dispatch = useLifeDispatch();
  const [idx, setIdx] = useState(0);
  const [answer, setAnswer] = useState<null | boolean>(null); // chosen "isAsset?"
  const [score, setScore] = useState(0);
  const [claimed, setClaimed] = useState(false);
  const [done, setDone] = useState(false);

  const item = DECK[idx]!;
  const correct = answer !== null && answer === item.isAsset;

  const choose = (asAsset: boolean) => {
    if (answer !== null) return;
    setAnswer(asAsset);
    if (asAsset === item.isAsset) setScore((s) => s + 1);
  };

  const claim = () => {
    const a: LifeAsset = {
      id: newId(item.type),
      type: item.type,
      name: item.name,
      value: item.value,
      monthlyIncome: item.monthlyIncome,
      monthlyCost: item.monthlyCost,
    };
    dispatch({ type: 'ADD_ASSET', asset: a });
    setClaimed(true);
  };

  const next = () => {
    if (idx >= DECK.length - 1) {
      setDone(true);
      return;
    }
    setIdx((i) => i + 1);
    setAnswer(null);
    setClaimed(false);
  };

  const restart = () => {
    setIdx(0);
    setAnswer(null);
    setScore(0);
    setClaimed(false);
    setDone(false);
  };

  if (done) {
    return (
      <Card style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 40 }}>{score >= DECK.length - 1 ? '🏆' : '👍'}</div>
        <h2 style={{ margin: '6px 0 2px', fontSize: 20, fontWeight: 900, color: 'var(--text)' }}>
          {score} / {DECK.length} spotted
        </h2>
        <p style={{ margin: '0 0 14px', fontSize: 13.5, color: 'var(--text-2)' }}>
          The rule never changes: <b>assets feed you, liabilities bleed you.</b>
        </p>
        <Button tone="primary" onClick={restart}>Play again</Button>
      </Card>
    );
  }

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Eyebrow color="var(--brand)">Asset or liability?</Eyebrow>
        <span style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 700 }}>{idx + 1}/{DECK.length} · score {score}</span>
      </div>

      <div style={{ display: 'grid', placeItems: 'center', gap: 6, padding: '16px 0 12px' }}>
        <div style={{ fontSize: 48 }}>{item.icon}</div>
        <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text)', textAlign: 'center' }}>{item.name}</div>
      </div>

      {answer === null ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Button tone="up" size="lg" onClick={() => choose(true)}>📈 Asset</Button>
          <Button tone="ghost" size="lg" onClick={() => choose(false)}>📉 Liability</Button>
        </div>
      ) : (
        <AnimatePresence>
          <motion.div key="fb" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
            <div
              style={{
                padding: 12,
                borderRadius: 14,
                background: correct ? 'var(--up-soft)' : 'var(--down-soft)',
                border: `1px solid ${correct ? 'var(--up)' : 'var(--down)'}`,
              }}
            >
              <div style={{ fontWeight: 900, color: correct ? 'var(--up)' : 'var(--down)', marginBottom: 2 }}>
                {correct ? 'Correct! ' : 'Not quite — '}
                it’s {item.isAsset ? 'an ASSET 📈' : 'a LIABILITY 📉'}
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.45 }}>{item.why}</div>
              {item.isAsset && (
                <div style={{ fontSize: 12.5, color: 'var(--up)', marginTop: 4, fontWeight: 700 }}>
                  +{money(item.monthlyIncome)}/mo income · worth {money(item.value)}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              {item.isAsset && !claimed ? (
                <Button tone="up" onClick={claim} full>➕ Add to my portfolio</Button>
              ) : item.isAsset && claimed ? (
                <Button tone="soft" full onClick={() => {}}>✓ Added — Freedom Meter moved!</Button>
              ) : null}
              <Button tone="primary" onClick={next} full={!item.isAsset || claimed}>
                {idx >= DECK.length - 1 ? 'Finish' : 'Next →'}
              </Button>
            </div>
          </motion.div>
        </AnimatePresence>
      )}
    </Card>
  );
}
